import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Outlet, useLocation } from 'react-router-dom'
import processAdobePageView from 'src/analytics/adobe/pageView'
import { UserType } from 'src/analytics/adobe/types'
import { DropdownObject } from 'src/components/Dropdown/Dropdown'
import MainContent from 'src/components/layouts/MainContent'
import MainMenu from 'src/components/layouts/MainMenu'
import MobileMenu from 'src/components/Navigation/MobileMenu/MobileMenu'
import { QuestionnaireMenu } from 'src/components/Questionnaire'
import useAITracking from 'src/rsmCoreComponents/hooks/useAITracking'
import useDeviceType from 'src/rsmCoreComponents/hooks/useDeviceType'
import { isCemFeatureEnabled } from 'src/rsmCoreComponents/utils/featureFlagUtils'
import { getCemFeatures, getUserInfo } from 'src/store/userInfo/userInfoSlice'
import { CEM_FEATURE_QUESTIONNAIRE_ASSIGN } from 'src/utils/constants/constants'

function parseLocationKey(path: string): string {
  const fragments = path?.split('/')
  if (fragments?.length > 1) return fragments[2]
  return path ?? ''
}

const QuestionnaireBaseLayout = () => {
  const { t } = useTranslation()
  const userInfo = useSelector(getUserInfo)
  const cemFeatures = useSelector(getCemFeatures)
  const userType: UserType = userInfo.isEmployee ? 'Internal' : 'External'
  const { pathname } = useLocation()
  const { isMobile } = useDeviceType()

  const mobileDropdownData: DropdownObject[] = useMemo(() => {
    const isAssignor = isCemFeatureEnabled(
      CEM_FEATURE_QUESTIONNAIRE_ASSIGN,
      cemFeatures,
      'any',
    )
    const menuItems = []

    if (isAssignor) {
      menuItems.push({
        value: `/questionnaires/queue`,
        display: t('Navigation.Questionnaire.SearchAssign'),
      })
    }
    return menuItems
  }, [cemFeatures])

  useAITracking(t('Questionnaire.PageTitle'), window.location.href)

  useEffect(() => {
    document.title = t('Questionnaire.PageTitle')
  }, [])

  useEffect(() => {
    if (pathname?.length > 0) processAdobePageView(pathname, userType)
  }, [pathname])

  return (
    <>
      <MainMenu
        currentItem="questionnaires"
        subNavSlot={
          isMobile && (
            <MobileMenu
              dropdownData={mobileDropdownData}
              currentSelected={parseLocationKey(pathname)}
              dropdownName="QuestionnairesSubNavigation"
              dropdownAriaLabel="Questionnaires Sub Navigation"
              ariaLabel="questionnaires menu"
            />
          )
        }
      />
      {!isMobile && (
        <QuestionnaireMenu currentItem={parseLocationKey(pathname)} />
      )}
      <MainContent>
        <Outlet />
      </MainContent>
    </>
  )
}

export default QuestionnaireBaseLayout
